import { ReactNode } from 'react';
import {
	Anchor,
	Badge,
	Image,
	Burger,
	Button,
	Center,
	Divider,
	Group,
	MantineProvider as Mantine,
	MantineThemeOverride,
	Menu,
	Modal,
	NativeSelect,
	Pagination,
	Radio,
	RadioGroup,
	Select,
	Tooltip,
	Notification,
	Stack,
	Checkbox,
	Popover,
	Accordion,
	BackgroundImage,
	Title,
	Drawer,
	Highlight,
	RangeSlider,
	Slider,
	Progress,
	Overlay,
	LoadingOverlay,
	PasswordInput,
	Text,
	Textarea,
	TextInput,
	Loader as MantineLoader,
	NumberInput,
	SegmentedControl,
	Chip,
	Table,
	Flex,
	Stepper,
	Indicator,
	Card,
} from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import { DatePicker } from '@mantine/dates';
import { Loader } from '@/components/ui/loading/partials/Loader';
import { IconChevronDown, IconEye, IconX } from '@tabler/icons-react';
import APP_CONFIG from '@/configs/appConfig';
import classNames from 'classnames';
import styles from '../sass/mantine.module.scss';
import '@mantine/carousel/styles.css';
import '@mantine/core/styles.layer.css';
import '@mantine/dates/styles.layer.css';
import '@mantine/dropzone/styles.layer.css';

const components: MantineThemeOverride['components'] = {
	Carousel: Carousel.extend({
		classNames: {
			container: styles.carouselContainer,
		},
	}),
	Card: Card.extend({
		defaultProps: {
			radius: 'sm',
			bg: 'gray.0',
			p: 'lg',
		},
		classNames: {},
	}),
	Flex: Flex.extend({
		defaultProps: {
			align: 'center',
		},
	}),
	Chip: Chip.extend({
		defaultProps: {
			radius: 'sm',
			size: 'md',
		},
		classNames: {
			label: styles.chipLabel,
		},
	}),
	Stepper: Stepper.extend({
		defaultProps: {
			radius: 'xl',
			iconSize: 24,
			bg: 'gray.0',
		},
		classNames: {
			separator: styles.stepperSeparator,
			root: styles.stepperRoot,
			steps: styles.stepperSteps,
			step: styles.stepperStep,
			stepIcon: styles.stepperStepIcon,
		},
	}),
	Pagination: Pagination.extend({
		defaultProps: {
			withControls: true,
			gap: 'sm',
			radius: 'sm',
		},
		classNames: {
			control: styles.paginationControl,
			dots: styles.paginationDots,
		},
	}),
	Text: Text.extend({
		defaultProps: {},
	}),
	Burger: Burger.extend({
		defaultProps: {
			color: 'green.9',
			size: 30,
		},
	}),
	Badge: Badge.extend({
		defaultProps: {
			radius: 'sm',
			px: 'sm',
			py: 'xs',
			color: 'dark.6',
		},
		classNames: {
			label: styles.badgeLabel,
		},
	}),
	Modal: Modal.extend({
		defaultProps: {
			centered: true,
			withCloseButton: true,
			radius: 'sm',
			zIndex: APP_CONFIG.Z_INDEX.MODAL,
			closeButtonProps: {
				size: 24,
				icon: <IconX />,
			},
			size: APP_CONFIG.BREAKPOINTS.XL,
			closeOnClickOutside: false,
		},
		classNames: {
			root: styles.modalRoot,
			inner: styles.modalInner,
			content: styles.modalContent,
			header: styles.modalHeader,
			body: styles.modalBody,
			close: styles.modalClose,
			title: styles.modalTitle,
		},
	}),
	SegmentedControl: SegmentedControl.extend({
		defaultProps: {
			size: 'md',
			radius: 'sm',
			color: 'lime',
		},
		classNames: {
			root: styles.segmentedControlRoot,
			label: styles.segmentedControlLabel,
		},
	}),
	Button: Button.extend({
		defaultProps: {
			radius: 'sm',
			size: 'md',
			loaderProps: {
				type: 'dots',
			},
		},
		classNames: {
			root: styles.buttonRoot,
		},
	}),
	Menu: Menu.extend({
		defaultProps: {
			position: 'bottom',
			shadow: 'sm',
			radius: 'sm',
		},
		classNames: {
			dropdown: styles.menuDropdown,
			item: styles.menuItem,
			divider: styles.menuDivider,
			itemLabel: styles.menuItemLabel,
			itemSection: styles.menuItemSection,
		},
	}),
	Divider: Divider.extend({
		defaultProps: {
			size: 1,
		},
		classNames: {
			label: styles.dividerLabel,
		},
	}),
	Indicator: Indicator.extend({
		defaultProps: {
			radius: 'xl',
		},
		classNames: {
			indicator: styles.indicatorIndicator,
		},
	}),
	Select: Select.extend({
		defaultProps: {
			radius: 'sm',
			size: 'sm',
			withCheckIcon: false,
			allowDeselect: false,
			rightSection: <IconChevronDown size={18} />,
			comboboxProps: {
				transitionProps: {
					duration: 100,
					transition: 'scale-y',
					timingFunction: 'ease',
				},
				zIndex: APP_CONFIG.Z_INDEX.SELECT,
			},
		},
		classNames: {
			input: styles.selectInput,
			label: styles.selectLabel,
			option: styles.selectOption,
			dropdown: styles.selectDropdown,
			empty: styles.selectEmpty,
		},
	}),
	Image: Image.extend({
		defaultProps: {
			radius: 'sm',
			fit: 'contain',
		},
		classNames: {
			root: 'fade-in',
		},
	}),
	NativeSelect: NativeSelect.extend({
		defaultProps: {
			rightSection: <IconChevronDown size={18} />,
			radius: 'sm',
			size: 'md',
		},
		classNames: {
			input: styles.selectInput,
			section: styles.selectRightSection,
		},
	}),
	Tooltip: Tooltip.extend({
		defaultProps: {
			position: 'top',
			transitionProps: {
				transition: 'pop',
				duration: 200,
			},
			multiline: true,
			withArrow: true,
			arrowSize: 6,
			events: { hover: true, focus: true, touch: true },
			color: 'black',
			opacity: 0.9,
			radius: 'sm',
			zIndex: APP_CONFIG.Z_INDEX.TOOLTIP,
		},
		classNames: {
			tooltip: styles.tooltipTooltip,
		},
	}),
	Group: Group.extend({
		classNames: {
			root: styles.groupRoot,
		},
	}),
	RadioGroup: RadioGroup.extend({}),
	Radio: Radio.extend({
		defaultProps: {
			size: 'xs',
			color: '#f9871b',
		},
		classNames: {
			root: styles.radioRoot,
			body: styles.radioBody,
			labelWrapper: styles.radioLabelWrapper,
			label: styles.radioLabel,
		},
	}),
	Anchor: Anchor.extend({
		defaultProps: {
			underline: 'always',
			c: 'black',
			fz: 16,
			fw: 400,
		},
	}),
	Stack: Stack.extend({
		classNames: {
			root: styles.stackRoot,
		},
	}),
	Checkbox: Checkbox.extend({
		classNames: {
			inner: styles.checkboxInner,
			label: styles.checkboxLabel,
		},
		defaultProps: {
			size: 'md',
			color: 'dark',
			radius: 'sm',
		},
	}),
	TextInput: TextInput.extend({
		defaultProps: {
			radius: 'sm',
			size: 'md',
			withAsterisk: false,
		},
		classNames: {
			input: styles.textInputInput,
			label: styles.textInputLabel,
			error: styles.textInputError,
		},
	}),
	NumberInput: NumberInput.extend({
		defaultProps: {
			radius: 'sm',
			size: 'sm',
		},
		classNames: {
			input: styles.textInputInput,
			label: styles.textInputLabel,
			error: styles.textInputError,
		},
	}),
	Textarea: Textarea.extend({
		defaultProps: {
			size: 'sm',
			radius: 'sm',
			autosize: true,
			minRows: 4,
			maxRows: 8,
		},
		classNames: {
			label: styles.textInputLabel,
		},
	}),
	PasswordInput: PasswordInput.extend({
		defaultProps: {
			radius: 'sm',
			visibilityToggleIcon: ({ reveal }) => (
				<Center
					className={classNames({
						[styles.passwordInputIconRevealed]: reveal,
					})}
				>
					<IconEye />
				</Center>
			),
		},
		classNames: {
			wrapper: styles.passwordInputWrapper,
			input: styles.passwordInputInput,
			section: styles.passwordInputSection,
			error: styles.passwordInputError,
		},
	}),
	Loader: MantineLoader.extend({
		defaultProps: {
			loaders: { ...MantineLoader.defaultLoaders, spinner: Loader },
			type: 'spinner',
		},
	}),
	LoadingOverlay: LoadingOverlay.extend({
		defaultProps: {
			loaderProps: {
				type: 'spinner',
			},
			overlayProps: {
				opacity: 0.5,
			},
			zIndex: APP_CONFIG.Z_INDEX.OVERLAY,
		},
	}),
	Overlay: Overlay.extend({
		defaultProps: {
			zIndex: APP_CONFIG.Z_INDEX.OVERLAY,
			backgroundOpacity: 0.25,
		},
	}),
	Progress: Progress.extend({
		defaultProps: {
			size: 'md',
			color: 'green.6',
			radius: 'xs',
		},
		styles: ({ colors }) => ({
			root: {
				backgroundColor: colors.green[0],
			},
		}),
	}),
	Slider: Slider.extend({
		defaultProps: {
			labelTransitionProps: {
				transition: 'fade',
				duration: 250,
			},
			thumbSize: 22,
			size: 6,
		},
		classNames: {
			root: styles.sliderRoot,
			bar: styles.sliderBar,
			thumb: styles.sliderThumb,
			track: styles.sliderTrack,
			label: styles.sliderLabel,
			markLabel: styles.sliderMarkLabel,
		},
	}),
	RangeSlider: RangeSlider.extend({
		defaultProps: {
			color: 'dark.5',
			labelTransitionProps: {
				transition: 'fade',
				duration: 250,
			},
			minRange: 1,
		},
	}),
	Highlight: Highlight.extend({
		defaultProps: {
			color: 'dark',
			highlightStyles: {
				fontWeight: 700,
			},
		},
	}),
	DatePicker: DatePicker.extend({
		classNames: {
			day: styles.datePickerDay,
		},
	}),
	Table: Table.extend({
		defaultProps: {
			horizontalSpacing: 'md',
			striped: true,
			withTableBorder: true,
		},
		classNames: {},
	}),
	Drawer: Drawer.extend({
		defaultProps: {
			position: 'right',
			zIndex: APP_CONFIG.Z_INDEX.DRAWER,
			size: 492,
			withCloseButton: true,
		},
		classNames: {
			overlay: styles.drawerInner,
			content: styles.drawerContent,
			inner: styles.drawerInner,
			body: styles.drawerBody,
			header: styles.drawerHeader,
			title: styles.drawerTitle,
			close: styles.drawerClose,
		},
	}),
	Title: Title.extend({
		defaultProps: {
			lts: -0.25,
			c: 'gray.8',
		},
	}),
	Notification: Notification.extend({
		defaultProps: { radius: 'sm', withCloseButton: false },
		styles: {
			root: {
				zIndex: APP_CONFIG.Z_INDEX.NOTIFICATION,
			},
		},
		classNames: {
			root: styles.notificationRoot,
			icon: styles.notificationIcon,
			title: styles.notificationTitle,
			closeButton: styles.notificationCloseButton,
		},
	}),
	BackgroundImage: BackgroundImage.extend({
		defaultProps: {},
	}),
	Accordion: Accordion.extend({
		defaultProps: {
			chevronPosition: 'left',
			chevronSize: 24,
			chevron: <IconChevronDown />,
			transitionDuration: 250,
		},
		classNames: {
			content: styles.accordionContent,
			item: styles.accordionItem,
			control: styles.accordionControl,
			label: styles.accordionLabel,
			chevron: styles.accordionChevron,
		},
	}),
	Popover: Popover.extend({
		defaultProps: {
			withArrow: true,
			shadow: 'md',
			radius: 'sm',
			zIndex: APP_CONFIG.Z_INDEX.POPOVER,
		},
		classNames: {
			dropdown: styles.popoverDropdown,
		},
	}),
};

const headings: MantineThemeOverride['headings'] = {
	fontFamily: 'Inter, sans-serif',
	fontWeight: '600',
};

const breakpoints = Object.entries(APP_CONFIG.BREAKPOINTS).reduce<
	MantineThemeOverride['breakpoints']
>((acc, curr) => {
	acc![curr[0].toLowerCase()] = curr[1];
	return acc;
}, {});

const colors: MantineThemeOverride['colors'] = {
	lime: [
		'#f4fce3',
		'#e9fac8',
		'#d8f5a2',
		'#c0eb75',
		'#a9e34b',
		'#94d82d',
		'#82c91e',
		'#74b816',
		'#72ac00',
		'#5c940d',
	],
};

export const MantineProvider = ({ children }: { children: ReactNode }) => {
	return (
		<Mantine
			theme={{
				fontFamily: 'Inter, sans-serif',
				white: '#ffffff',
				black: '#141414',
				colors,
				cursorType: 'pointer',
				components,
				headings,
				breakpoints,
				primaryColor: 'lime',
				primaryShade: 8,
			}}
		>
			{children}
		</Mantine>
	);
};
